import React, { Component } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import s from './stars.module.scss'
import { SemanticUIColors } from './semanticUIColors'
import { Star } from './star'

interface Props {
  averageRating?: number
  clickHandler: (selectedRating: number) => void
  currentlySelectedRating: number
  inverted: boolean
  starAmount: number
  starColor: SemanticUIColors
  votingIsOver: boolean
}

interface State {
  activeStar: number
}

/**
 * Appends stars and returns the event when clicking on them
 *
 * @param {Props} props
 * @param {number} [props.averageRating] The rating value after rating has
 * been done.
 * @param {Function} props.clickHandler
 * @param {boolean} [props.inverted=false]
 * @param {number} props.currentlySelectedRating The currently chosen rating by
 *   the user.
 * @param {number} [props.starAmount=5] The amount of stars to show and choose
 * from.
 * @param {SemanticUIColors} [props.starColor=yellow] The color of the icons
 * @param {boolean} [props.votingIsOver=false] Whether the user is allowed
 * to vote
 */
export class Stars extends Component<Props, State> {
  public static defaultProps = {
    starAmount: 5,
    starColor: 'yellow',
    inverted: false,
    currentlySelectedRating: -1,
    votingIsOver: false
  }

  public constructor(props: Props) {
    super(props)

    this.state = {
      activeStar: -1
    }
  }

  private onMouseLeave = (enteredRating: number) => {
    this.setState({
      activeStar: -1
    })
  }

  private onMouseEnter = (enteredRating: number) => {
    this.setState({
      activeStar: enteredRating
    })
  }

  public render() {
    let starArray = []
    let additionalHalfStar = false
    let floorStars = 0

    // These are the default events. They will fire in any case. The both
    // have "preventDefault" enabled, so in case the onTouchEnd fires,
    // onClick won't be fired.
    let events: { [key: string]: (arg0: number) => void } = {
      onTouchEnd: this.props.clickHandler,
      onClick: this.props.clickHandler
    }

    // This is used to decided whether we have a touch device (which does
    // not allow hovering) or another devices which allows it. In case it is
    // allowed, attach the onMouseEnter / -Leave event handlers
    //
    // @todo look at possibility to use pointer events instead (aren't
    //       currently supported on Safari, though)
    if (window.matchMedia('(hover: hover)').matches) {
      events = {
        ...events,
        onMouseEnter: this.onMouseEnter,
        onMouseLeave: this.onMouseLeave
      }
    }

    // Check if rating contains half stars or not
    if (this.props.averageRating) {
      floorStars = Math.floor(this.props.averageRating)

      if (
        this.props.averageRating &&
        this.props.averageRating - floorStars >= 0.25 &&
        this.props.averageRating - floorStars < 0.75
      ) {
        additionalHalfStar = true
      }

      if (
        this.props.averageRating &&
        this.props.averageRating - floorStars >= 0.75
      ) {
        floorStars = floorStars + 1
      }
    }

    // Check if rating is set from Question component and also check for
    // MouseOver
    for (let i = 1; i <= this.props.starAmount; i++) {
      let starClass = 'invisible'
      if (
        this.state.activeStar >= i ||
        this.props.currentlySelectedRating >= i
      ) {
        starClass = ''
      }
      if (this.props.votingIsOver) {
        starClass = 'over'
        if (floorStars < i) {
          starClass = 'invisible'
          if (additionalHalfStar && i - 1 === floorStars) {
            starClass = 'over half'
          }
        }
      }

      starArray.push(
        <Star
          key={`star-${i}`}
          i={i}
          inverted={this.props.inverted}
          events={events}
          starColor={this.props.starColor}
          starClass={starClass}
        />
      )
    }
    return (
      <div
        className={`stars ${s.stars} ${this.props.starColor} ${
          this.props.inverted ? 'inverted' : ''
        }`}
      >
        {starArray}

        {this.props.votingIsOver && this.props.averageRating && (
          <div
            className={`label average ${s.averageLabel} ${
              this.props.inverted ? 'inverted' : ''
            }`}
          >
            ⌀{' '}
            {this.props.averageRating.toLocaleString('fr-CH', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1
            })}
          </div>
        )}
      </div>
    )
  }
}

export default Stars
